import React from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { evaluationCopy } from '../../utils/evaluationCopy';
import { auditTextList, getEvaluationPresentation } from '../../utils/evaluationPresentation';
import { numericScore } from '../../utils/evaluationStatus';
import { translateEvaluationText } from '../../utils/evaluationText';
import './EvaluationSummary.css';

export function ReportDisclosure({ title, children, className = '' }) {
  return <details className={`evaluation-disclosure ${className}`} data-print-detail>
    <summary>{title}</summary>
    <div className="evaluation-disclosure-body">{children}</div>
  </details>;
}

function SectionTotals({ sections }) {
  const { language, t } = useLanguage();
  return <dl className="evaluation-section-totals" aria-label={evaluationCopy(language).sectionTotals}>
    {sections.map(section => <div key={section.key}>
      <dt>{t(section.labelKey)}</dt>
      <dd>{section.score === null ? '—' : section.score}/{section.max}</dd>
    </div>)}
  </dl>;
}

function FeedbackList({ title, items, ordered = false }) {
  const { language } = useLanguage();
  if (!items.length) return null;
  const List = ordered ? 'ol' : 'ul';
  return <section className="evaluation-feedback" aria-label={title}>
    <h3>{title}</h3>
    <List>{items.map((item, index) => <li key={index}>{translateEvaluationText(item, language)}</li>)}</List>
  </section>;
}

function SectionFeedback({ detail }) {
  const { language, t } = useLanguage();
  const copy = evaluationCopy(language);
  return <>
    <FeedbackList title={t('didWell')} items={auditTextList(detail.achieved)} />
    <FeedbackList title={t('partialCredit')} items={auditTextList(detail.partial)} />
    <FeedbackList title={copy.missed} items={auditTextList(detail.missed)} />
    <FeedbackList title={t('marksLost')} items={auditTextList(detail.lost_marks_reason)} />
    <FeedbackList title={t('howImprove')} items={auditTextList(detail.improvements)} />
  </>;
}

const hasSectionFeedback = detail => ['achieved', 'partial', 'missed', 'lost_marks_reason', 'improvements']
  .some(key => auditTextList(detail[key]).length > 0);
const points = value => numericScore(value) ?? '—';

function CriterionEvidence({ item }) {
  const { language } = useLanguage();
  const copy = evaluationCopy(language);
  const evidence = Array.isArray(item.evidence) ? item.evidence.filter(entry => entry && typeof entry.quote === 'string') : [];
  if (evidence.length) return <ul className="evaluation-evidence-list">{evidence.map((entry, index) => <li key={index}>
    {typeof entry.source_id === 'string' && <small>{copy.source}: {entry.source_id}</small>}
    <q>{entry.quote}</q>
  </li>)}</ul>;
  return typeof item.quote === 'string' && item.quote.trim() ? <q>{item.quote}</q> : copy.noEvidence;
}

function ScoringDetails({ presentation }) {
  const { language, t } = useLanguage();
  const copy = evaluationCopy(language);
  const hasDetails = presentation.feedback || presentation.improvementPlan.length ||
    presentation.sections.some(section => section.items.length || hasSectionFeedback(section.detail));
  if (!hasDetails) return null;
  return <ReportDisclosure title={copy.scoringDetails}>
    <section aria-label={t('aiBreakdown')}>
      {(presentation.evaluatorVersion || presentation.rubricVersion) && <p className="evaluation-version">
        {presentation.evaluatorVersion && <span>{copy.evaluator}: {presentation.evaluatorVersion}</span>}
        {presentation.rubricVersion && <span>{copy.rubric}: {presentation.rubricVersion}</span>}
      </p>}
      {presentation.sections.map(section => <article className="evaluation-detail-section" key={section.key}>
        <h3>{t(section.labelKey)} <span>{section.score === null ? '—' : section.score}/{section.max}</span></h3>
        {section.items.length > 0 && <div className="evaluation-rubric-scroll">
          <table className="evaluation-rubric">
            <thead><tr><th>{copy.criterion}</th><th>{copy.credit}</th><th>{copy.evidence}</th></tr></thead>
            <tbody>{section.items.map((item, index) => <tr key={index}>
              <th scope="row">{translateEvaluationText(item.item, language) || '—'}
                {typeof item.criterion === 'string' && item.criterion.trim() && <details className="evaluation-criterion" data-print-detail><summary>{copy.criterionDetails}</summary><p>{translateEvaluationText(item.criterion, language)}</p></details>}
                {typeof item.reason === 'string' && item.reason.trim() && <p><strong>{copy.reason}: </strong>{translateEvaluationText(item.reason, language)}</p>}
                {typeof item.next_step === 'string' && item.next_step.trim() && <p><strong>{copy.nextStep}: </strong>{translateEvaluationText(item.next_step, language)}</p>}
              </th>
              <td data-label={copy.credit}>{points(item.points_awarded)} / {points(item.max_points)}
                {numericScore(item.weight) !== null && <small className="evaluation-rubric-weight">{copy.weight}: {numericScore(item.weight)}</small>}
              </td>
              <td data-label={copy.evidence}><CriterionEvidence item={item} /></td>
            </tr>)}</tbody>
          </table>
        </div>}
        {section.items.length && hasSectionFeedback(section.detail)
          ? <ReportDisclosure title={copy.sectionFeedback}><SectionFeedback detail={section.detail} /></ReportDisclosure>
          : <SectionFeedback detail={section.detail} />}
      </article>)}
      {presentation.feedback && <section className="evaluation-feedback"><h3>{copy.originalFeedback}</h3><p>{translateEvaluationText(presentation.feedback, language)}</p></section>}
      <FeedbackList title={t('nextPriorities')} items={presentation.improvementPlan} ordered />
    </section>
  </ReportDisclosure>;
}

export default function EvaluationSummary({ evaluation, fallbackScore, instructorReview = null, archived = false }) {
  const { language, t } = useLanguage();
  const copy = evaluationCopy(language);
  const presentation = getEvaluationPresentation(evaluation, fallbackScore);

  if (instructorReview) return <div className="compact-evaluation">
    <section className="evaluation-summary-card" aria-label={t('doctorReview')}>
      <div className="evaluation-score-heading">
        <div><h2>{copy.instructorScore}</h2><p>{copy.completed}</p></div>
        <div className="evaluation-total" aria-label={`${copy.instructorScore}: ${instructorReview.total}/100`}><strong>{instructorReview.total}</strong><span>/100</span></div>
      </div>
      <SectionTotals sections={instructorReview.sections} />
      {instructorReview.comment && <section className="evaluation-instructor-comment"><h3>{t('notesComments')}</h3><p>{instructorReview.comment}</p></section>}
    </section>
    <ReportDisclosure title={copy.automatedAssessment}>
      <section aria-label={copy.automatedAssessment}><EvaluationSummary evaluation={evaluation} fallbackScore={fallbackScore} archived /></section>
    </ReportDisclosure>
  </div>;

  if (presentation.pending) return <section className="evaluation-summary-card evaluation-pending">
    {archived ? <p>{copy.automatedUnavailable}</p> : <><h2 role="status">{t('awaitingReview')}</h2><p>{t('reviewPendingMessage')}</p></>}
    {presentation.pendingReason && <p>{translateEvaluationText(presentation.pendingReason, language)}</p>}
  </section>;

  return <div className="compact-evaluation">
    <section className="evaluation-summary-card" aria-label={t('clinicalEvaluation')}>
      <div className="evaluation-score-heading">
        <div><h2>{t('aiScore')}</h2><p>{translateEvaluationText(presentation.summary, language) || copy.summaryFallback}</p></div>
        <div className="evaluation-total" aria-label={`${t('aiScore')}: ${presentation.total}/100`}><strong>{presentation.total}</strong><span>/100</span></div>
      </div>
      <SectionTotals sections={presentation.sections} />
      {(presentation.strengths.length > 0 || presentation.improvements.length > 0) && <div className="evaluation-feedback-grid">
        <FeedbackList title={copy.strengths} items={presentation.strengths} />
        <FeedbackList title={copy.improvements} items={presentation.improvements} ordered />
      </div>}
    </section>
    <ScoringDetails presentation={presentation} />
  </div>;
}
